/**
 * Vorgangsuebersicht in der Partnerakte: offene Posten, letzte Belege,
 * Reklamationen.
 *
 * Jede Karte prueft ihre eigene Berechtigung - wer etwa keine Buchhaltung
 * sehen darf, bekommt die offenen Posten schlicht nicht angezeigt (die
 * eigentliche Pruefung sitzt wie immer am Server). Die Karten zeigen nur
 * die juengsten Eintraege; die vollstaendigen Listen bleiben auf ihren
 * eigenen Seiten.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
  COMPLAINT_STATUS_LABELS,
  formatAmount,
  formatDate,
  getDocumentType,
  isOverdue,
  type ComplaintStatus,
  type DocumentType,
} from '@erp/shared';

import { api, withQuery } from '../api/client';
import { useAuth } from '../auth';
import { DOCUMENT_ROUTES, StatusBadge, type DocumentListItem } from '../pages/DocumentsPage';
import { CreditGauge } from './CreditGauge';
import { Badge, CollapsibleTitle, LoadingBlock, useCollapsed } from './ui';

export function PartnerRecords({
  partnerId,
  role,
  creditLimit = 0,
}: {
  partnerId: string;
  role: 'customer' | 'supplier';
  /** Kreditlimit in Cent, fuer die Ausschoepfung in den offenen Posten. */
  creditLimit?: number;
}) {
  const { can, moduleEnabled } = useAuth();
  const side = role === 'customer' ? 'sales' : 'purchase';

  return (
    <>
      {can('accounting:read') || can('accounting:open-items') ? (
        <OpenItemsCard partnerId={partnerId} side={side} creditLimit={creditLimit} />
      ) : null}
      {can(side === 'sales' ? 'sales:read' : 'purchase:read') ? (
        <DocumentsCard partnerId={partnerId} side={side} />
      ) : null}
      {role === 'customer' && moduleEnabled('complaints') && can('complaints:read') ? (
        <ComplaintsCard partnerId={partnerId} />
      ) : null}
    </>
  );
}

// --- Offene Posten -----------------------------------------------------------

type OpenItemsResult = {
  items: {
    id: string;
    type: DocumentType;
    number: string | null;
    documentDate: string;
    dueDate: string | null;
    grossTotal: number;
    paidAmount: number;
  }[];
  openTotal: number;
  overdueTotal: number;
};

function OpenItemsCard({
  partnerId,
  side,
  creditLimit,
}: {
  partnerId: string;
  side: 'sales' | 'purchase';
  creditLimit: number;
}) {
  const navigate = useNavigate();
  const [collapsed, toggle] = useCollapsed('offene-posten');

  const { data, isLoading } = useQuery({
    queryKey: ['open-items', side, partnerId],
    queryFn: () =>
      api.get<OpenItemsResult>(withQuery('/api/accounting/open-items', { side, partnerId })),
  });

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-body">
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: collapsed ? 0 : 10 }}>
          <CollapsibleTitle title="Offene Posten" collapsed={collapsed} onToggle={toggle} />
          {data && data.openTotal > 0 ? (
            <div className="row" style={{ gap: 12, alignItems: 'center' }}>
              <span>
                Offen: <strong>{formatAmount(data.openTotal)} €</strong>
              </span>
              {data.overdueTotal > 0 ? (
                <span style={{ color: 'var(--danger)' }}>
                  überfällig: <strong>{formatAmount(data.overdueTotal)} €</strong>
                </span>
              ) : null}
              {side === 'sales' ? (
                <CreditGauge creditLimit={creditLimit} openAmount={data.openTotal} />
              ) : null}
            </div>
          ) : null}
        </div>

        {collapsed ? null : isLoading || !data ? (
          <LoadingBlock />
        ) : data.items.length === 0 ? (
          <div className="faint">Keine offenen Posten.</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="shrink">Beleg</th>
                  <th className="shrink">Datum</th>
                  <th className="shrink">Fällig</th>
                  <th className="numeric">Brutto</th>
                  <th className="numeric">Offen</th>
                </tr>
              </thead>
              <tbody>
                {data.items.slice(0, 10).map((row) => {
                  const overdue = row.dueDate !== null && isOverdue(row.dueDate);
                  return (
                    <tr
                      key={row.id}
                      className="clickable"
                      onClick={() => navigate(`${DOCUMENT_ROUTES[row.type]}/${row.id}`)}
                    >
                      <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                        {getDocumentType(row.type).label} {row.number}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.documentDate)}</td>
                      <td
                        style={
                          overdue
                            ? { color: 'var(--danger)', fontWeight: 600, whiteSpace: 'nowrap' }
                            : { whiteSpace: 'nowrap' }
                        }
                      >
                        {formatDate(row.dueDate)}
                      </td>
                      <td className="numeric">{formatAmount(row.grossTotal)}</td>
                      <td className="numeric" style={{ fontWeight: 500 }}>
                        {formatAmount(row.grossTotal - row.paidAmount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {data.items.length > 10 ? (
              <div className="faint" style={{ marginTop: 8 }}>
                10 von {data.items.length} offenen Belegen — alle unter Buchhaltung → Zahlungen.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Letzte Belege -----------------------------------------------------------

function DocumentsCard({ partnerId, side }: { partnerId: string; side: 'sales' | 'purchase' }) {
  const navigate = useNavigate();
  const [collapsed, toggle] = useCollapsed('letzte-belege');

  const { data, isLoading } = useQuery({
    queryKey: ['documents', 'partner', side, partnerId],
    queryFn: () =>
      api.get<{ items: DocumentListItem[]; total: number }>(
        withQuery('/api/documents', {
          side,
          partnerId,
          limit: 10,
          sort: 'documentDate',
          dir: 'desc',
        }),
      ),
  });

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-body">
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: collapsed ? 0 : 10 }}>
          <CollapsibleTitle title="Letzte Belege" collapsed={collapsed} onToggle={toggle} />
          {data && data.total > data.items.length ? (
            <span className="faint">{data.items.length} von {data.total}</span>
          ) : null}
        </div>

        {collapsed ? null : isLoading || !data ? (
          <LoadingBlock />
        ) : data.items.length === 0 ? (
          <div className="faint">Noch keine Belege.</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="shrink">Beleg</th>
                  <th className="shrink">Datum</th>
                  <th>Betreff</th>
                  <th className="numeric">Brutto</th>
                  <th className="shrink">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => {
                  const overdue =
                    (row.type === 'invoice' || row.type === 'purchase_invoice') &&
                    row.lifecycle === 'posted' &&
                    row.dueDate !== null &&
                    row.paidAmount < row.grossTotal &&
                    isOverdue(row.dueDate);
                  return (
                    <tr
                      key={row.id}
                      className="clickable"
                      onClick={() => navigate(`${DOCUMENT_ROUTES[row.type]}/${row.id}`)}
                    >
                      <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                        {getDocumentType(row.type).label}{' '}
                        {row.number ?? <span className="faint">Entwurf</span>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.documentDate)}</td>
                      <td className="muted">{row.subject ?? ''}</td>
                      <td className="numeric">{formatAmount(row.grossTotal)}</td>
                      <td>
                        <StatusBadge document={row} overdue={overdue} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Reklamationen -----------------------------------------------------------

type ComplaintListRow = {
  id: string;
  number: string;
  receivedDate: string;
  reason: string;
  status: ComplaintStatus;
};

function ComplaintsCard({ partnerId }: { partnerId: string }) {
  const navigate = useNavigate();
  const [collapsed, toggle] = useCollapsed('reklamationen');

  const { data, isLoading } = useQuery({
    queryKey: ['complaints', 'partner', partnerId],
    queryFn: () =>
      api.get<{ items: ComplaintListRow[]; total: number }>(
        withQuery('/api/complaints', { partnerId, limit: 10 }),
      ),
  });

  // Ohne Vorgaenge keine Karte - das ist bei den meisten Kunden der Fall
  // und eine leere Reklamationsliste ist keine Information.
  if (isLoading || !data || data.items.length === 0) return null;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-body">
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: collapsed ? 0 : 10 }}>
          <CollapsibleTitle title="Reklamationen" collapsed={collapsed} onToggle={toggle} />
          {data.total > data.items.length ? (
            <span className="faint">{data.items.length} von {data.total}</span>
          ) : null}
        </div>

        {collapsed ? null : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th className="shrink">Nummer</th>
                <th className="shrink">Eingang</th>
                <th>Beanstandung</th>
                <th className="shrink">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr
                  key={row.id}
                  className="clickable"
                  onClick={() => navigate('/reklamationen')}
                >
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>{row.number}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.receivedDate)}</td>
                  <td
                    style={{
                      maxWidth: 420,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.reason}
                  </td>
                  <td>
                    <Badge
                      kind={
                        row.status === 'open'
                          ? 'warning'
                          : row.status === 'in_progress'
                            ? 'accent'
                            : row.status === 'resolved'
                              ? 'success'
                              : 'danger'
                      }
                    >
                      {COMPLAINT_STATUS_LABELS[row.status]}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}
